import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../../store';
import { setPreferences, getUserInfo, completeOnboarding } from '../../store/actions/OnboardingActions';
import { fetchMatterDropdowns } from '../../store/actions/Matters/MatterActions';
import Button from '../shared/TailwindComponents/Button';
import { AVAILABLE_JURISDICTIONS } from '../Matters/matterConstants';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { PiSunLight, PiMoonLight, PiDesktopLight } from 'react-icons/pi';
import apis from '../../utils/apis';
import { changeLanguage } from '../../i18n/i18n';

const SUPPORTED_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'sv', label: 'Svenska' },
  { value: 'es', label: 'Español' },
  { value: 'nb', label: 'Norsk Bokmål' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fi', label: 'Suomi' },
  { value: 'da', label: 'Dansk' },
  { value: 'he', label: 'עברית' },
];

const PreferencesSetStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const onboarding = useSelector((state: RootState) => state.onboarding);
  const [skipPreferences, setSkipPreferences] = useState(false);
  const [preferencesData, setPreferencesData] = useState({
    general_settings: {
      language: 'en',
      theme: 'system',
    },
    ai_settings: {
      jurisdiction: 'SE',
      language: 'en',
    },
  });
  const [aiLanguages, setAiLanguages] = useState<DropdownOption[]>([]);
  const [jurisdictionOptions, setJurisdictionOptions] = useState<DropdownOption[]>([]);

  // Fetch AI languages and jurisdictions from backend
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const dropdownsData = await dispatch(fetchMatterDropdowns());
        if (dropdownsData) {
          setAiLanguages(dropdownsData.languages || []);
          setJurisdictionOptions(AVAILABLE_JURISDICTIONS);
        }
      } catch (error) {
        console.error('Failed to fetch dropdowns:', error);
      }
    };

    fetchDropdowns();
  }, [dispatch]);

  // Guard: This step should only be accessible if organization is set
  // Other fields (first_name, last_name, role) are validated at the page level
  if (!onboarding.user?.organization_id) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-light dark:shadow-dark p-8">
        <p className="text-gray-600 dark:text-gray-400">{t('Please complete the organization and profile steps first.')}</p>
      </div>
    );
  }

  const applyThemeToDOM = (theme: string) => {
    const root = window.document.documentElement;
    let themeToApply = theme;

    // If system theme is selected, detect the user's preference
    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      themeToApply = prefersDark ? 'dark' : 'light';
    }

    root.classList.remove('light', 'dark');
    root.classList.add(themeToApply);
    localStorage.setItem('theme', theme);
  };

  const handleGeneralSettingChange = (field: string, value: any) => {
    setPreferencesData((prev) => ({
      ...prev,
      general_settings: {
        ...prev.general_settings,
        [field]: value,
      },
    }));

    // Apply theme immediately to the DOM
    if (field === 'theme') {
      applyThemeToDOM(value);
    }

    // Apply language change immediately
    if (field === 'language') {
      changeLanguage(value);
    }
  };

  const handleAISettingChange = (field: string, value: any) => {
    setPreferencesData((prev) => ({
      ...prev,
      ai_settings: {
        ...prev.ai_settings,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!skipPreferences) {
        await dispatch(setPreferences(preferencesData) as any);
      } else {
        // If skipping preferences, still dispatch success to mark onboarding as complete
        dispatch({
          type: 'ONBOARDING_SET_PREFERENCES_SUCCESS',
          payload: {
            preferences: preferencesData,
          },
        });
      }

      // Mark onboarding as completed in the backend
      await dispatch(completeOnboarding() as any);

      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 w-full max-w-2xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-light dark:shadow-dark p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('Platform Settings')}</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-white mb-2">
              {t('Language')}
            </label>
            <Dropdown
              placeholder={t('Select language')}
              options={SUPPORTED_LANGUAGES}
              value={preferencesData.general_settings.language}
              fullWidth={true}
              onChange={(value) => handleGeneralSettingChange('language', value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-white mb-2">
              {t('Theme')}
            </label>
            <div className="flex gap-2">
              {[
                { value: 'light', icon: PiSunLight, label: t('Light') },
                { value: 'dark', icon: PiMoonLight, label: t('Dark') },
                { value: 'system', icon: PiDesktopLight, label: t('System') },
              ].map(({ value, icon: Icon, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleGeneralSettingChange('theme', value)}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                    preferencesData.general_settings.theme === value
                      ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary-300'
                  }`}
                  title={label}
                >
                  <Icon size={16} />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-light dark:shadow-dark p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('AI Settings')}</h3>

        <div className="space-y-4">
          <div>
            <Dropdown
              label={t('Jurisdiction')}
              placeholder={t('Select jurisdiction')}
              options={jurisdictionOptions}
              value={preferencesData.ai_settings.jurisdiction}
              onChange={(value) => handleAISettingChange('jurisdiction', value)}
              fullWidth={true}
            />
          </div>

          <div>
            <Dropdown
              label={t('AI Language')}
              placeholder={t('Select AI language')}
              options={aiLanguages}
              value={preferencesData.ai_settings.language}
              onChange={(value) => handleAISettingChange('language', value)}
              fullWidth={true}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 w-full max-w-2xl mx-auto">
        <input
          type="checkbox"
          id="skip-prefs"
          checked={skipPreferences}
          onChange={(e) => setSkipPreferences(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="skip-prefs" className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
          {t('Skip preferences for now')}
        </label>
      </div>

      <div className="flex justify-end w-full max-w-2xl mx-auto pb-4">
        <Button
          type="submit"
          variant="primary"
          size="small"
          disabled={onboarding.isLoading}
          isLoading={onboarding.isLoading}
          className="w-full md:w-auto"
        >
          {t('Complete Setup')}
        </Button>
      </div>
    </form>
  );
};

export default PreferencesSetStep;
